import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';
import CatalogItemModal from '../components/CatalogItemModal';

export default function Products() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => { load(); loadCategories(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/products/');
      setItems(res.data);
    } catch { toast.error('Erro ao carregar produtos'); }
    finally { setLoading(false); }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get('/settings/');
      setCategories(res.data.categorias || []);
    } catch {}
  };

  const openModal = (item = null) => {
    setEditing(item);
    setShowModal(true);
  };

  const saveProduct = async (payload, editingId) => {
    if (editingId) {
      const res = await api.put(`/products/${editingId}`, payload);
      return res.data;
    }
    const res = await api.post('/products/', payload);
    return res.data;
  };

  const del = async (id) => {
    if (!window.confirm('Excluir produto?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Produto excluído');
      load();
    } catch { toast.error('Erro ao excluir'); }
  };

  const filtered = items.filter(i =>
    !search || [i.descricao, i.codigo, i.categoria].some(f => f?.toLowerCase().includes(search.toLowerCase()))
  );

  const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Produtos</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Catálogo da sua empresa</p>
        </div>
        <button data-testid="new-product-btn" onClick={() => openModal()}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold px-4 py-2 rounded-xl transition-colors">
          <Plus size={17} /> Novo produto
        </button>
      </div>

      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input className="field-input pl-9" placeholder="Buscar produto..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-800">
            <tr className="text-xs text-zinc-500">
              <th className="text-left px-4 py-3 hidden sm:table-cell">Código</th>
              <th className="text-left px-4 py-3">Descrição</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Categoria</th>
              <th className="text-right px-4 py-3">Custo</th>
              <th className="text-right px-4 py-3">Venda</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12"><div className="inline-block w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></td></tr>
            ) : filtered.map(item => (
              <tr key={item.id} data-testid={`product-row-${item.id}`} className="table-row-hover">
                <td className="px-4 py-3 font-mono text-xs text-zinc-500 hidden sm:table-cell">{item.codigo || '—'}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-white">{item.descricao}</div>
                  {item.complemento && <div className="text-xs text-zinc-500">{item.complemento}</div>}
                </td>
                <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{item.categoria || '—'}</td>
                <td className="px-4 py-3 text-right font-mono text-zinc-400">{brl(item.custo)}</td>
                <td className="px-4 py-3 text-right font-mono text-orange-400 font-medium">{brl(item.valor)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => openModal(item)} className="p-1.5 text-zinc-500 hover:text-orange-400 transition-colors"><Edit size={14} /></button>
                    <button onClick={() => del(item.id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 text-zinc-500 text-sm">Nenhum produto encontrado</div>
        )}
      </div>

      <CatalogItemModal
        open={showModal}
        kind="produto"
        editing={editing}
        categories={categories}
        onSave={saveProduct}
        onClose={() => setShowModal(false)}
        onSaved={load}
      />
    </div>
  );
}
